/**
 * Shared utilities for forecast calculations and formatting
 */

/**
 * Format month as abbreviated string
 */
function formatMonth(date: Date): string {
const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[date.getMonth()];
}

/**
 * Create a short period label for chart display
 * Yearly: "2026"
 * Quarterly: "Sep 2025" (start month)
 * Monthly: "Sep" or "Jan '26" (show year when it changes)
 */
export function createShortPeriodLabel(
  startDate: string | Date,
  granularityMonths: number,
  periodIndex: number,
  previousYear?: number
): { label: string; currentYear: number } {
  const baseDate = typeof startDate === 'string' ? new Date(startDate) : startDate;
  
  // Calculate period start
  // periodIndex represents the starting month offset for this period
  const periodStart = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  periodStart.setMonth(periodStart.getMonth() + periodIndex);
  
  // Calculate period end for yearly
  const periodEnd = new Date(periodStart);
  periodEnd.setMonth(periodEnd.getMonth() + granularityMonths - 1);
  
  const currentYear = periodStart.getFullYear();
  
  if (granularityMonths === 12) {
    // Yearly: Show the start year for the period
    // Jan 2025 - Dec 2025 → "2025"
    // Jul 2025 - Jun 2026 → "2025"
    const startYear = periodStart.getFullYear();
    return { label: startYear.toString(), currentYear: startYear };
  } else if (granularityMonths === 3) {
    // Quarterly: "Sep 2025"
    return { label: `${formatMonth(periodStart)} ${currentYear}`, currentYear };
  } else {
    // Monthly: Show year only when it changes
    let label = formatMonth(periodStart);
    if (previousYear !== undefined && previousYear !== currentYear) {
      // Year changed, show it
      label += ` '${currentYear.toString().slice(-2)}`;
    } else if (periodIndex === 0) {
      // First period, always show year
      label += ` ${currentYear}`;
    }
    return { label, currentYear };
  }
}

/**
 * Get the end date of a period
 */
export function getPeriodEndDate(
  startDate: string | Date,
  granularityMonths: number,
  periodIndex: number
): Date {
  const baseDate = typeof startDate === 'string' ? new Date(startDate) : startDate;
  
  // Calculate period start
  const periodStart = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  periodStart.setMonth(periodStart.getMonth() + periodIndex);
  
  // Calculate period end (last day of last month in period)
  const periodEnd = new Date(periodStart);
  periodEnd.setMonth(periodEnd.getMonth() + granularityMonths);
  periodEnd.setDate(0); // Go to last day of previous month
  
  return periodEnd;
}

/**
 * Create a range-based period label (e.g., "Sep 2025 - Aug 2026")
 * This is the new standard for clear period labeling
 */
export function createPeriodRangeLabel(
  startDate: string | Date,
  granularityMonths: number,
  periodIndex: number
): string {
  const baseDate = typeof startDate === 'string' ? new Date(startDate) : startDate;
  
  // Calculate period start (add months from base date)
  const periodStart = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  periodStart.setMonth(periodStart.getMonth() + periodIndex);
  
  // Calculate period end (last month of the period)
  const periodEnd = new Date(periodStart);
  periodEnd.setMonth(periodEnd.getMonth() + granularityMonths - 1);
  
  // Format based on granularity
  if (granularityMonths === 12) {
    // Yearly: "Sep 2025 - Aug 2026" (always show both years)
    return `${formatMonth(periodStart)} ${periodStart.getFullYear()} - ${formatMonth(periodEnd)} ${periodEnd.getFullYear()}`;
  } else if (granularityMonths === 3) {
    // Quarterly: omit year if same
    if (periodStart.getFullYear() === periodEnd.getFullYear()) {
      // Same year: "Sep - Nov 2025"
      return `${formatMonth(periodStart)} - ${formatMonth(periodEnd)} ${periodStart.getFullYear()}`;
    } else {
      // Cross year: "Nov 2025 - Jan 2026"
      return `${formatMonth(periodStart)} ${periodStart.getFullYear()} - ${formatMonth(periodEnd)} ${periodEnd.getFullYear()}`;
    }
  } else {
    // Monthly: "Sep 2025"
    return `${formatMonth(periodStart)} ${periodStart.getFullYear()}`;
  }
}

/**
 * Format a period label for display based on context
 * Chart labels are handled separately with createShortPeriodLabel
 * This returns the full range label for tooltips and other displays
 */
export function formatPeriodLabel(timePeriod: string, _granularityMonths: number): string {
  // Range labels are already well-formatted, just return as-is
  return timePeriod;
}

/**
 * Get human-readable period type name
 */
export function getPeriodTypeName(granularityMonths: number): string {
  if (granularityMonths === 12) return 'Year';
  if (granularityMonths === 3) return 'Quarter';
  return 'Month';
}

/**
 * Format period for table display based on granularity and result data
 */
export function formatPeriodForTable(
  result: { timePeriod: string; periodStartDate: string; granularityMonths?: number },
  _fallbackGranularity?: number
): string {
  // Range labels are already formatted, just return the timePeriod
  return result.timePeriod;
}